import { type SetStateAction, type WritableAtom, atom } from "jotai";

export type Location = {
	pathname?: string;
	searchParams?: URLSearchParams;
	hash?: string;
};

const getLocation = (): Location => {
	if (typeof window === "undefined" || !window.location) {
		return {};
	}
	return {
		pathname: window.location.pathname,
		searchParams: new URLSearchParams(window.location.search),
		hash: window.location.hash,
	};
};

const subscribe = (callback: () => void) => {
	window.addEventListener("popstate", callback);
	return () => window.removeEventListener("popstate", callback);
};

export type Options<T> = {
	preloaded?: T;
	replace?: boolean;
	getLocation?: () => T;
	applyLocation?: (location: T, options?: { replace?: boolean }) => void;
	subscribe?: (callback: () => void) => () => void;
};

type RequiredOptions<T> = Omit<Options<T>, "getLocation" | "applyLocation"> &
	Required<Pick<Options<T>, "getLocation" | "applyLocation">>;

type AtomOptions<T> = Pick<Options<T>, "replace">;

export function atomWithLocation(
	options?: Options<Location>
): WritableAtom<Location, [SetStateAction<Location>, AtomOptions<Location>?], void>;

export function atomWithLocation<T>(
	options: RequiredOptions<T>
): WritableAtom<T, [SetStateAction<T>, AtomOptions<T>?], void>;

export function atomWithLocation<T>(options?: Options<T>) {
	const getL =
		options?.getLocation || (getLocation as unknown as NonNullable<Options<T>["getLocation"]>);
	const sub = options?.subscribe || subscribe;
	const baseAtom = atom(options?.preloaded ?? getL());

	if (process.env["NODE_ENV"] !== "production") {
		baseAtom.debugPrivate = true;
	}

	baseAtom.onMount = (set) => {
		const callback = () => set(getL());
		const unsub = sub(callback);
		callback();
		return unsub;
	};
	const derivedAtom = atom(
		(get) => get(baseAtom),
		(_get, set, arg: SetStateAction<T>) => {
			set(baseAtom, arg);
		}
	);
	return derivedAtom;
}

/**
 * The current route of the application
 * @example {pathname: "/d/{workspaceName}/{documentId}/{pageSlug}", searchParams: URLSearchParams, hash: ""}
 */
export const $location = atomWithLocation();
$location.debugLabel = "location";

export const $locationWorkspaceName = atom((get) => {
	const location = get($location);
	const workspaceName = location.pathname?.split("/").at(2);

	return workspaceName;
});
$locationWorkspaceName.debugLabel = "locationWorkspaceName";

export const $locationDocumentId = atom((get) => {
	const location = get($location);
	const documentId = location.pathname?.split("/").at(3);

	return documentId;
});
$locationDocumentId.debugLabel = "locationDocumentId";

export const $locationPageSlug = atom((get) => {
	const location = get($location);
	// FIXME: This is not at all correct. If we have a nested page, this gets some random other
	// page's ID.
	const pageSlug = location.pathname?.split("/").at(4);

	return pageSlug;
});
$locationPageSlug.debugLabel = "locationPageSlug";
